import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { 
  Users, Edit, Plus, Search, Shield, UserCog, Truck, 
  Trash2, Eye, EyeOff, Filter, ChevronDown, ChevronUp,
  UserPlus, Check, X, Lock, KeyRound
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTION_PASSWORD = '5445';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: { Authorization: `Bearer ${token}` }
  };
};

export default function UserManagement() {
  // Password protection
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortField, setSortField] = useState('username');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Edit dialog
  const [editingUser, setEditingUser] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    is_mechanic: false,
    mechanic_access: 'mechanic_only',
    is_driver: false,
    role: 'user',
    new_password: ''
  });
  const [showEditPassword, setShowEditPassword] = useState(false);
  
  // Create dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'user',
    is_mechanic: false,
    mechanic_access: 'mechanic_only',
    is_driver: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [creating, setCreating] = useState(false);

  // Delete confirmation
  const [deleteUser, setDeleteUser] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Check session storage for unlock status
  useEffect(() => {
    const unlocked = sessionStorage.getItem('userManagementUnlocked');
    if (unlocked === 'true') {
      setIsUnlocked(true);
    }
  }, []);

  useEffect(() => {
    if (isUnlocked) {
      loadUsers();
    }
  }, [isUnlocked]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordInput === SECTION_PASSWORD) {
      setIsUnlocked(true);
      setPasswordError(false);
      sessionStorage.setItem('userManagementUnlocked', 'true');
      toast.success('Доступ разрешён');
    } else {
      setPasswordError(true);
      toast.error('Неверный пароль');
    }
  };

  // Password protection screen
  if (!isUnlocked) {
    return (
      <div className="flex items-center justify-center py-16">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mb-4 shadow-lg">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-800">
              Защищённый раздел
            </CardTitle>
            <p className="text-gray-500 mt-2">
              Введите пароль для доступа к управлению пользователями
            </p>
          </CardHeader>
          <CardContent className="pt-4">
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Пароль</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type={showPasswordInput ? 'text' : 'password'}
                    placeholder="Введите пароль"
                    value={passwordInput}
                    onChange={(e) => {
                      setPasswordInput(e.target.value);
                      setPasswordError(false);
                    }}
                    className={`pl-10 pr-10 ${passwordError ? 'border-red-500 focus:ring-red-500' : ''}`}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordInput(!showPasswordInput)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswordInput ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {passwordError && (
                  <p className="text-red-500 text-sm mt-1">Неверный пароль</p>
                )}
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                <Lock className="w-4 h-4 mr-2" />
                Разблокировать
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, getAuthHeaders());
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Ошибка загрузки пользователей');
      setLoading(false);
    }
  };

  // Filter and sort users
  const filteredUsers = users
    .filter(user => {
      const matchesSearch = 
        user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase());
      
      if (roleFilter === 'all') return matchesSearch;
      if (roleFilter === 'superadmin') return matchesSearch && user.role === 'superadmin';
      if (roleFilter === 'admin') return matchesSearch && (user.role === 'admin' || user.role === 'superadmin');
      if (roleFilter === 'mechanic') return matchesSearch && user.is_mechanic;
      if (roleFilter === 'driver') return matchesSearch && user.is_driver;
      return matchesSearch;
    })
    .sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-4 h-4 ml-1" /> : 
      <ChevronDown className="w-4 h-4 ml-1" />;
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    setEditForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      is_mechanic: user.is_mechanic || false,
      mechanic_access: user.mechanic_access || 'mechanic_only',
      is_driver: user.is_driver || false,
      role: user.role || 'user',
      new_password: ''
    });
    setShowEditPassword(false);
    setShowEditDialog(true);
  };

  const handleSaveRoles = async () => {
    try {
      // Prepare data - only include password if it was changed
      const updateData = {
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        is_mechanic: editForm.is_mechanic,
        mechanic_access: editForm.mechanic_access,
        is_driver: editForm.is_driver,
        role: editForm.role
      };
      
      // Add new password if provided
      if (editForm.new_password && editForm.new_password.trim()) {
        updateData.password = editForm.new_password.trim();
      }
      
      await axios.put(
        `${API}/users/${editingUser.id}`,
        updateData,
        getAuthHeaders()
      );
      
      const message = editForm.new_password 
        ? 'Данные пользователя и пароль обновлены' 
        : 'Права пользователя обновлены';
      toast.success(message);
      setShowEditDialog(false);
      setEditingUser(null);
      loadUsers();
    } catch (error) {
      console.error('Error updating user roles:', error);
      toast.error(error.response?.data?.detail || 'Ошибка обновления прав');
    }
  };

  const handleCreateUser = async () => {
    if (!createForm.username || !createForm.email || !createForm.password) {
      toast.error('Заполните все обязательные поля');
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(`${API}/users`, createForm, getAuthHeaders());
      toast.success('Пользователь создан');
      setShowCreateDialog(false);
      setCreateForm({
        username: '',
        email: '',
        password: '',
        role: 'user',
        is_mechanic: false,
        mechanic_access: 'mechanic_only',
        is_driver: false
      });
      loadUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Ошибка создания пользователя');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteClick = (user) => {
    setDeleteUser(user);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`${API}/users/${deleteUser.username}`, getAuthHeaders());
      toast.success('Пользователь удалён');
      setShowDeleteDialog(false);
      setDeleteUser(null);
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Ошибка удаления пользователя');
    }
  };

  const getRoleBadges = (user) => {
    const badges = [];
    if (user.role === 'superadmin') {
      badges.push(
        <span key="superadmin" className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
          <Shield className="w-3 h-3" />
          Супер-админ
        </span>
      );
    } else if (user.role === 'admin') {
      badges.push(
        <span key="admin" className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
          <Shield className="w-3 h-3" />
          Админ
        </span>
      );
    }
    if (user.is_mechanic) {
      badges.push(
        <span key="mechanic" className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
          <UserCog className="w-3 h-3" />
          Механик
          {user.mechanic_access === 'full_access' && <span className="ml-1 text-[10px] opacity-70">(полный)</span>}
        </span>
      );
    }
    if (user.is_driver) {
      badges.push(
        <span key="driver" className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
          <Truck className="w-3 h-3" />
          Водитель
        </span>
      );
    }
    if (badges.length === 0) {
      badges.push(
        <span key="user" className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
          Пользователь
        </span>
      );
    }
    return badges;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-gray-600">Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <Card className="bg-gradient-to-r from-blue-500 to-indigo-600 border-0 shadow-xl">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Пользователи</h2>
                <p className="text-white/80 text-sm">Всего: {users.length}</p>
              </div>
            </div>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-white text-blue-600 hover:bg-blue-50 font-semibold shadow-lg"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Добавить
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Поиск по имени или email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            
            {/* Role Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Все роли</option>
                <option value="superadmin">Супер-админы</option>
                <option value="admin">Администраторы</option>
                <option value="mechanic">Механики</option>
                <option value="driver">Водители</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th 
                    className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('username')}
                  >
                    <div className="flex items-center">
                      Пользователь
                      <SortIcon field="username" />
                    </div>
                  </th>
                  <th 
                    className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 hidden sm:table-cell"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center">
                      Email
                      <SortIcon field="email" />
                    </div>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Роли
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm">
                          {(user.first_name || user.username).charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {user.first_name || user.last_name 
                              ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                              : user.username}
                          </p>
                          <p className="text-xs text-gray-500">@{user.username}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">
                      {user.email}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {getRoleBadges(user)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditClick(user)}
                          className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600"
                          title="Редактировать"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteClick(user)}
                          className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600"
                          title="Удалить"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {filteredUsers.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Пользователи не найдены</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Редактировать: {editingUser?.username}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Name fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold mb-1 block text-gray-700">Имя</Label>
                <Input
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                  placeholder="Иван"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold mb-1 block text-gray-700">Фамилия</Label>
                <Input
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                  placeholder="Петров"
                />
              </div>
            </div>

            {/* Main Role */}
            <div>
              <Label className="text-sm font-semibold mb-2 block text-gray-700">Основная роль</Label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="role"
                    checked={editForm.role === 'superadmin'}
                    onChange={() => setEditForm({ ...editForm, role: 'superadmin' })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <Shield className="w-4 h-4 text-purple-500" />
                  <span className="font-medium text-sm">Супер-администратор</span>
                </label>

                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="role"
                    checked={editForm.role === 'admin'}
                    onChange={() => setEditForm({ ...editForm, role: 'admin' })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <Shield className="w-4 h-4 text-red-500" />
                  <span className="font-medium text-sm">Администратор</span>
                </label>
                
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="role"
                    checked={editForm.role === 'user'}
                    onChange={() => setEditForm({ ...editForm, role: 'user' })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="font-medium text-sm">Обычный пользователь</span>
                </label>
              </div>
            </div>

            {/* Additional Roles */}
            <div>
              <Label className="text-sm font-semibold mb-2 block text-gray-700">Дополнительные роли</Label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={editForm.is_mechanic}
                    onChange={(e) => setEditForm({ ...editForm, is_mechanic: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <UserCog className="w-4 h-4 text-orange-500" />
                  <span className="font-medium text-sm">Механик</span>
                </label>

                {editForm.is_mechanic && (
                  <div className="ml-8 p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <Label className="text-xs font-semibold text-orange-700 mb-2 block">Доступ механика</Label>
                    <select
                      value={editForm.mechanic_access}
                      onChange={(e) => setEditForm({ ...editForm, mechanic_access: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="mechanic_only">Только страница механика</option>
                      <option value="full_access">Полный доступ к проекту</option>
                    </select>
                  </div>
                )}

                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={editForm.is_driver}
                    onChange={(e) => setEditForm({ ...editForm, is_driver: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <Truck className="w-4 h-4 text-green-500" />
                  <span className="font-medium text-sm">Водитель</span>
                </label>
              </div>
            </div>

            {/* Password Change */}
            <div>
              <Label className="text-sm font-semibold mb-2 block text-gray-700">
                <KeyRound className="w-4 h-4 inline mr-1" />
                Смена пароля
              </Label>
              <div className="p-3 border rounded-lg bg-gray-50">
                <div className="relative">
                  <Input
                    type={showEditPassword ? 'text' : 'password'}
                    value={editForm.new_password}
                    onChange={(e) => setEditForm({ ...editForm, new_password: e.target.value })}
                    placeholder="Оставьте пустым, чтобы не менять"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowEditPassword(!showEditPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showEditPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {editForm.new_password && (
                  <p className="text-xs text-orange-600 mt-2 flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Пароль будет изменён при сохранении
                  </p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-3">
              <Button
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                className="flex-1"
              >
                Отмена
              </Button>
              <Button
                onClick={handleSaveRoles}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <Check className="w-4 h-4 mr-2" />
                Сохранить
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-600" />
              Новый пользователь
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Username */}
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Логин *</Label>
              <Input
                placeholder="Введите логин"
                value={createForm.username}
                onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
              />
            </div>

            {/* Name fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Имя</Label>
                <Input
                  placeholder="Иван"
                  value={createForm.first_name}
                  onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Фамилия</Label>
                <Input
                  placeholder="Петров"
                  value={createForm.last_name}
                  onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Email *</Label>
              <Input
                type="email"
                placeholder="email@example.com"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              />
            </div>

            {/* Password */}
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Пароль *</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Введите пароль"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Role */}
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Основная роль</Label>
              <select
                value={createForm.role}
                onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="user">Пользователь</option>
                <option value="admin">Администратор</option>
                <option value="superadmin">Супер-администратор</option>
              </select>
            </div>

            {/* Additional Roles */}
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Дополнительные роли</Label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={createForm.is_mechanic}
                    onChange={(e) => setCreateForm({ ...createForm, is_mechanic: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <UserCog className="w-4 h-4 text-orange-500" />
                  <span className="text-sm">Механик</span>
                </label>

                {createForm.is_mechanic && (
                  <div className="ml-6 p-2 bg-orange-50 rounded-lg border border-orange-200">
                    <select
                      value={createForm.mechanic_access}
                      onChange={(e) => setCreateForm({ ...createForm, mechanic_access: e.target.value })}
                      className="w-full border rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="mechanic_only">Только страница механика</option>
                      <option value="full_access">Полный доступ</option>
                    </select>
                  </div>
                )}

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={createForm.is_driver}
                    onChange={(e) => setCreateForm({ ...createForm, is_driver: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <Truck className="w-4 h-4 text-green-500" />
                  <span className="text-sm">Водитель</span>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-3">
              <Button
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                className="flex-1"
                disabled={creating}
              >
                Отмена
              </Button>
              <Button
                onClick={handleCreateUser}
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={creating}
              >
                {creating ? (
                  <>Создание...</>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Создать
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Удалить пользователя?
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-gray-600">
              Вы уверены, что хотите удалить пользователя <strong>{deleteUser?.username}</strong>?
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Это действие нельзя отменить.
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              className="flex-1"
            >
              Отмена
            </Button>
            <Button
              onClick={handleConfirmDelete}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Удалить
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
